import React from 'react';
import { motion } from 'framer-motion';
import { UserPlusIcon, CreditCardIcon, BarChart3Icon, CheckCircle2Icon } from 'lucide-react';
const steps = [{
  number: '01',
  icon: UserPlusIcon,
  title: 'Sign Up in Minutes',
  description: 'Create your account with just your email. No lengthy forms or approval processes.',
  color: 'from-blue-500 to-cyan-500'
}, {
  number: '02',
  icon: CreditCardIcon,
  title: 'Connect Your Bank',
  description: 'Securely link your bank account for instant payouts. We handle all the compliance.',
  color: 'from-orange-500 to-pink-500'
}, {
  number: '03',
  icon: BarChart3Icon,
  title: 'Start Accepting',
  description: 'Get your donation page link and start collecting. It really is that simple.',
  color: 'from-purple-500 to-indigo-500'
}, {
  number: '04',
  icon: CheckCircle2Icon,
  title: 'Get Paid Instantly',
  description: 'Funds hit your account immediately. No waiting, no delays, no surprises.',
  color: 'from-green-500 to-emerald-500'
}];
export function HowItWorks() {
  return <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-id="element-200">
      {/* Animated Background */}
      <div className="absolute inset-0" data-id="element-201">
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'linear'
      }} className="absolute inset-0 opacity-10" style={{
        backgroundImage: `linear-gradient(to right, rgba(251, 146, 60, 0.3) 1px, transparent 1px),
                             linear-gradient(to bottom, rgba(251, 146, 60, 0.3) 1px, transparent 1px)`,
        backgroundSize: '64px 64px'
      }} data-id="element-202" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8" data-id="element-203">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-204">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-205">
            Go live in 4 simple steps
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-206">
            We've eliminated all the friction. Get started in less time than it
            takes to make coffee.
          </p>
        </motion.div>

        <div className="relative" data-id="element-207">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-orange-500 via-purple-500 to-green-500 opacity-20" data-id="element-208" />

          <div className="grid lg:grid-cols-4 gap-8" data-id="element-209">
            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.15
          }} className="relative" data-id="element-210">
                {/* Step Card */}
                <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700 hover:border-slate-600 transition-all group" data-id="element-211">
                  {/* Number Badge */}
                  <div className="absolute -top-4 left-8" data-id="element-212">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`} data-id="element-213">
                      <span className="text-white font-bold text-lg" data-id="element-214">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="mt-8 mb-6" data-id="element-215">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${step.color} opacity-10 group-hover:opacity-20 transition-opacity`} data-id="element-216">
                      <step.icon className="w-8 h-8 text-white" data-id="element-217" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-218">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed" data-id="element-219">
                    {step.description}
                  </p>

                  {/* Hover Effect */}
                  <motion.div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 transition-opacity -z-10`} data-id="element-220" />
                </div>

                {/* Arrow (desktop only) */}
                {index < steps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-8 -translate-y-1/2 z-10" data-id="element-221">
                    <svg className="w-full h-full text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-222">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-223" />
                    </svg>
                  </div>}
              </motion.div>)}
          </div>
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center mt-16" data-id="element-224">
          <button className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold hover:shadow-xl hover:shadow-orange-500/30 transition-all" data-id="element-225">
            Start Your Free Trial
          </button>
          <p className="text-slate-400 text-sm mt-4" data-id="element-226">
            No credit card required • Cancel anytime
          </p>
        </motion.div>
      </div>
    </section>;
}